import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User, ArrowLeft, Shield, Upload, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalEvidence = () => {
  const [consentRequests, setConsentRequests] = useState([]);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [consentForm, setConsentForm] = useState(null);
  const [extractions, setExtractions] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState('list'); // list, form, upload, extractions, threads
  const clientInfo = clientPortalService.getClientInfo();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [consents, exts] = await Promise.all([
        clientPortalService.getEvidenceConsentRequests(),
        clientPortalService.getEvidenceExtractions(),
      ]);
      setConsentRequests(consents);
      setExtractions(exts);
    } catch (err) {
      console.error('Failed to load evidence data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewConsent = async (consentId) => {
    try {
      const form = await clientPortalService.getEvidenceConsentForm(consentId);
      setConsentForm(form);
      setSelectedConsent(consentId);
      setView('form');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load consent form');
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      alert('Please enter your full legal name');
      return;
    }
    try {
      setSigning(true);
      await clientPortalService.signEvidenceConsent(selectedConsent, signatureName);
      alert('Consent signed successfully');
      setView('list');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to sign consent');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this consent request?')) return;
    try {
      await clientPortalService.declineEvidenceConsent(selectedConsent);
      setView('list');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const handleRevoke = async (consentId) => {
    if (!window.confirm('Are you sure you want to revoke your consent? This cannot be undone.')) return;
    try {
      await clientPortalService.revokeEvidenceConsent(consentId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedConsent) return;
    try {
      setUploading(true);
      await clientPortalService.uploadEvidenceFile(selectedConsent, uploadFile);
      alert('File uploaded successfully');
      setView('list');
      setUploadFile(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleViewThreads = async (extraction) => {
    try {
      setSelectedExtraction(extraction);
      const data = await clientPortalService.getEvidenceThreads(extraction.id);
      setThreads(data);
      setView('threads');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load threads');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'signed': return <CheckCircle className="text-green-400" size={18} />;
      case 'declined': case 'revoked': return <XCircle className="text-red-400" size={18} />;
      case 'expired': return <AlertTriangle className="text-slate-400" size={18} />;
      default: return <Shield className="text-amber-400" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-amber-300',
      sent: 'text-blue-300',
      viewed: 'text-purple-300',
      signed: 'text-green-300',
      declined: 'text-red-300',
      revoked: 'text-red-300',
      expired: 'text-slate-300',
    };
    return colors[status] || 'text-virgo-teal-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/portal/dashboard" className="text-virgo-teal-400 hover:text-white">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">Evidence Requests</h1>
              <p className="text-xs text-virgo-teal-400">Mobile Device Data Authorization</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/portal/profile-setup" className="text-virgo-teal-400 hover:text-white"><User size={20} /></Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* LIST VIEW */}
        {view === 'list' && (
          <div>
            {/* Consent Requests */}
            <h2 className="text-lg font-bold text-white mb-4">Consent Requests</h2>
            {consentRequests.length === 0 ? (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8 text-center">
                <Shield className="text-virgo-teal-500 mx-auto mb-3" size={40} />
                <p className="text-virgo-teal-400">No evidence requests at this time</p>
              </div>
            ) : (
              <div className="space-y-3 mb-8">
                {consentRequests.map(req => (
                  <div key={req.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(req.status)}
                        <div>
                          <p className="text-white font-medium">
                            {req.device_type?.toUpperCase()} Data Extraction Authorization
                          </p>
                          <p className="text-xs text-virgo-teal-500">
                            Case: {req.case_reference || '—'} | From: {req.requesting_attorney || '—'}
                          </p>
                          <p className={`text-xs mt-1 ${getStatusColor(req.status)}`}>
                            Status: {req.status}
                            {req.expires_at && ` | Expires: ${new Date(req.expires_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {['pending', 'sent', 'viewed'].includes(req.status) && (
                          <button onClick={() => handleViewConsent(req.id)}
                            className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700">
                            Review & Sign
                          </button>
                        )}
                        {req.status === 'signed' && (
                          <>
                            <button onClick={() => { setSelectedConsent(req.id); setView('upload'); }}
                              className="px-3 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg text-sm hover:bg-virgo-navy-500">
                              <Upload size={14} className="inline mr-1" /> Upload
                            </button>
                            <button onClick={() => handleRevoke(req.id)}
                              className="px-3 py-2 bg-red-600/20 text-red-300 rounded-lg text-sm hover:bg-red-600/30">
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Shared Extractions */}
            {extractions.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-white mb-4">Shared Results</h2>
                <div className="space-y-3">
                  {extractions.map(ext => (
                    <div key={ext.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-medium">{ext.device_type?.toUpperCase()} Extraction</p>
                          <p className="text-xs text-virgo-teal-500">
                            {ext.message_count} messages | {ext.thread_count} threads |
                            {ext.created_at ? ` ${new Date(ext.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <button onClick={() => handleViewThreads(ext)}
                          className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg text-sm hover:bg-virgo-navy-500">
                          View Threads
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONSENT FORM VIEW */}
        {view === 'form' && consentForm && (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
            <button onClick={() => setView('list')} className="text-virgo-teal-400 hover:text-white text-sm mb-4 flex items-center">
              <ArrowLeft size={16} className="mr-1" /> Back to list
            </button>

            {/* Consent Document */}
            <div className="bg-white rounded-lg p-8 text-gray-900 max-w-3xl mx-auto">
              <div className="text-center mb-6 border-b pb-4">
                <h2 className="text-2xl font-bold">{consentForm.firm_name}</h2>
                <h3 className="text-lg font-semibold mt-1">Authorization for Mobile Device Data Extraction</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Date: {consentForm.created_at ? new Date(consentForm.created_at).toLocaleDateString() : '—'}
                  {consentForm.case_reference && ` | Case: ${consentForm.case_reference}`}
                </p>
              </div>

              <div className="space-y-4 text-sm">
                {/* Parties */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">1. Parties</h4>
                  <p>Client: <strong>{consentForm.client_name || '—'}</strong></p>
                  <p>Firm: <strong>{consentForm.firm_name}</strong></p>
                  <p>Requesting Attorney: <strong>{consentForm.requesting_attorney || '—'}</strong></p>
                </div>

                {/* Authorization */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">2. Authorization Grant</h4>
                  <p>I, the undersigned, hereby authorize the extraction of SMS, MMS, and iMessage data from my
                    <strong> {consentForm.device_type === 'ios' ? 'Apple iOS' : 'Android'}</strong> mobile device
                    for use in connection with the above-referenced legal matter.</p>
                </div>

                {/* Scope */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">3. Scope of Extraction</h4>
                  {consentForm.scope === 'all' ? (
                    <p>This authorization covers <strong>all messages</strong> stored on the device.</p>
                  ) : (
                    <div>
                      <p>This authorization is limited to messages matching the following criteria:</p>
                      <ul className="list-disc list-inside mt-1 ml-2">
                        {consentForm.filter_contacts?.length > 0 && (
                          <li>Contacts: {consentForm.filter_contacts.join(', ')}</li>
                        )}
                        {consentForm.filter_date_start && (
                          <li>From: {new Date(consentForm.filter_date_start).toLocaleDateString()}</li>
                        )}
                        {consentForm.filter_date_end && (
                          <li>To: {new Date(consentForm.filter_date_end).toLocaleDateString()}</li>
                        )}
                        {consentForm.filter_keywords?.length > 0 && (
                          <li>Keywords: {consentForm.filter_keywords.join(', ')}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Data Handling */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">4. Data Handling & Retention</h4>
                  <ul className="list-disc list-inside ml-2">
                    <li>All extracted data will be stored in encrypted form</li>
                    <li>Data will be retained for <strong>{consentForm.data_retention_days} days</strong> from extraction</li>
                    <li>Data will be used solely in connection with this legal matter</li>
                    <li>Access is limited to the assigned case team</li>
                  </ul>
                </div>

                {/* Rights */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">5. Rights & Acknowledgments</h4>
                  <ul className="list-disc list-inside ml-2">
                    <li>I certify that I am the owner of or authorized user of the device</li>
                    <li>I understand the scope of data being extracted</li>
                    <li>I understand that communications with third parties may be included</li>
                    <li>I understand I have the right to revoke this consent at any time</li>
                  </ul>
                </div>

                {/* Revocation */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">6. Revocation</h4>
                  <p>This consent may be revoked at any time by notifying the firm through the client portal.
                    Revocation will prevent future extractions but will not affect data already lawfully extracted
                    under this authorization, which may be retained subject to legal hold obligations.</p>
                </div>

                <p className="text-xs text-gray-400 mt-4">Consent Version: {consentForm.consent_version}</p>
              </div>
            </div>

            {/* Signing Section */}
            {['pending', 'sent', 'viewed'].includes(consentForm.status) && (
              <div className="mt-6 bg-virgo-navy-800/50 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3">Digital Signature</h4>
                <div className="mb-3">
                  <label className="block text-sm text-virgo-teal-400 mb-1">Full Legal Name</label>
                  <input
                    type="text"
                    value={signatureName}
                    onChange={e => setSignatureName(e.target.value)}
                    className="w-full bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg px-3 py-2 text-white"
                    placeholder="Enter your full legal name"
                  />
                </div>
                <p className="text-xs text-virgo-teal-500 mb-3">
                  By signing below, you acknowledge that you have read and understood this authorization,
                  and that your electronic signature has the same legal effect as a handwritten signature.
                  Your IP address and timestamp will be recorded.
                </p>
                <div className="flex space-x-3">
                  <button onClick={handleDecline}
                    className="px-6 py-2 bg-red-600/20 text-red-300 rounded-lg hover:bg-red-600/30">
                    Decline
                  </button>
                  <button onClick={handleSign} disabled={signing || !signatureName.trim()}
                    className="flex-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    {signing ? 'Signing...' : 'Sign Authorization'}
                  </button>
                </div>
              </div>
            )}

            {consentForm.status === 'signed' && (
              <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <CheckCircle className="text-green-400 mx-auto mb-2" size={32} />
                <p className="text-green-300 font-medium">This consent has been signed</p>
              </div>
            )}

            {['declined', 'revoked'].includes(consentForm.status) && (
              <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <XCircle className="text-red-400 mx-auto mb-2" size={32} />
                <p className="text-red-300 font-medium">This consent has been {consentForm.status}</p>
              </div>
            )}
          </div>
        )}

        {/* UPLOAD VIEW */}
        {view === 'upload' && (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 max-w-lg mx-auto">
            <button onClick={() => setView('list')} className="text-virgo-teal-400 hover:text-white text-sm mb-4 flex items-center">
              <ArrowLeft size={16} className="mr-1" /> Back
            </button>
            <h3 className="text-lg font-bold text-white mb-4">Upload Backup File</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-1">Select your device backup file</label>
                <input type="file" onChange={e => setUploadFile(e.target.files[0])} required
                  className="w-full text-virgo-teal-300 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-cyan-600 file:text-white file:cursor-pointer" />
              </div>
              <p className="text-xs text-virgo-teal-500">
                Your file will be encrypted and virus-scanned upon upload. Only your legal team will have access.
              </p>
              <div className="flex space-x-3">
                <button type="button" onClick={() => setView('list')}
                  className="flex-1 px-4 py-2 border border-virgo-teal-700/50 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-600">Cancel</button>
                <button type="submit" disabled={uploading}
                  className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* THREADS VIEW */}
        {view === 'threads' && selectedExtraction && (
          <div>
            <button onClick={() => setView('list')} className="text-virgo-teal-400 hover:text-white text-sm mb-4 flex items-center">
              <ArrowLeft size={16} className="mr-1" /> Back
            </button>
            <h2 className="text-lg font-bold text-white mb-4">
              Extraction Threads ({threads.length})
            </h2>
            {threads.length === 0 ? (
              <p className="text-virgo-teal-500 text-center py-8">No threads found</p>
            ) : (
              <div className="space-y-2">
                {threads.map(thread => (
                  <div key={thread.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-medium">{thread.display_name || thread.thread_id}</p>
                        <p className="text-xs text-virgo-teal-500">
                          {thread.message_count} messages | {thread.participant_count} participants
                        </p>
                      </div>
                      <span className="text-xs text-virgo-teal-600">
                        {thread.last_message_at ? new Date(thread.last_message_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalEvidence;
